import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * localStorage key for the last completed search, so reopening search
   * after navigating to a result page (a full page load, not a dialog
   * close) can restore it instead of starting over.
   */
  static #SEARCH_CACHE_KEY = 'um:predictive-search:last-query';

  /** Cached searches older than this are treated as if there were none. */
  static #SEARCH_CACHE_TTL_MS = 60 * 60 * 1000;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#anchorToHeaderSearch, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // The empty state's tile grids are built from the predictive-search-empty
    // section's blocks, which only resolve when that section is actually
    // rendered server-side (the static markup here has no section context).
    // Always fetch it so real content is ready before the modal is opened —
    // unless there's a cached search from before the last page load, in
    // which case that's restored instead (see #loadInitialContent).
    requestIdleCallback(() => {
      this.#loadInitialContent();
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event. Deliberately does NOT clear the search
   * term/results — closing (Escape, clicking outside, the X button) should
   * leave the search as-is so reopening shows the same thing, matching the
   * "clear" button being the only thing that actually clears a search.
   * Keyboard-selection highlighting is transient UI state though, so that
   * still resets.
   */
  #handleDialogClose = () => {
    this.#currentIndex = -1;
    this.#anchoredTrigger?.style.removeProperty('visibility');
    this.#anchoredTrigger = null;
  };

  /** @type {HTMLElement | null} */
  #anchoredTrigger = null;

  /**
   * On laptop and larger, the modal's search field is laid exactly over the
   * header's search pill (which is hidden while open), so the header pill
   * behaves as the real input with the results panel hanging below it.
   * Smaller screens keep the regular modal layout.
   */
  #anchorToHeaderSearch = () => {
    this.removeAttribute('data-anchored');
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    const trigger = Array.from(document.querySelectorAll('.search-action__input-trigger')).find(
      (el) => el.getClientRects().length > 0
    );
    const form = this.querySelector('.predictive-search-form');
    if (!(trigger instanceof HTMLElement) || !form) return;

    const triggerRect = trigger.getBoundingClientRect();
    const formRect = form.getBoundingClientRect();

    this.style.setProperty('--pill-x', `${triggerRect.left - formRect.left}px`);
    this.style.setProperty('--pill-y', `${triggerRect.top - formRect.top}px`);
    this.style.setProperty('--pill-w', `${triggerRect.width}px`);
    this.style.setProperty('--pill-h', `${triggerRect.height}px`);
    this.setAttribute('data-anchored', '');

    trigger.style.visibility = 'hidden';
    this.#anchoredTrigger = trigger;
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded) {
      this.#loadInitialContent();
    }
  };

  /**
   * Loads whatever should be showing before the visitor has typed anything
   * this page load: a restored previous search if one is cached and still
   * fresh, otherwise the normal browse/empty state.
   */
  #loadInitialContent() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;

    if (this.#restoreSearchFromCache()) return;

    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys. Escape
   * isn't handled here — the dialog's own `closeDialogOnEscapePress` listener
   * (bound on the <dialog> element itself) closes it, and #handleDialogClose
   * deliberately leaves the search term/results alone.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          const typed = this.refs.searchInput.value;
          const corrected = window.applySearchSynonyms?.(typed) ?? typed;
          searchUrl.searchParams.set('q', window.quoteSearchTerms?.(corrected) ?? corrected);
          if (corrected !== typed) searchUrl.searchParams.set('synonym_from', typed);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const originalTerm = searchTerm;
    searchTerm = window.applySearchSynonyms?.(searchTerm) ?? searchTerm;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Shopify's predictive search API defaults resources[type] to "product"
    // alone when it's omitted, so collections/pages/articles never came back
    // without this — the dropdown was silently product-only before this fix.
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', '10');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    this.#setLoading(true);

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        if (searchTerm !== originalTerm) {
          const inner = predictiveSearchResults.querySelector('.predictive-search-results__inner');
          inner?.prepend(window.buildSearchSynonymNotice(searchTerm, originalTerm));
        }

        this.#resetScrollPositions();
        this.#writeSearchCache(searchTerm, resultsMarkup);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      })
      .finally(() => {
        // A superseded fetch's controller was already aborted by the newer
        // one's #createAbortController() call — only the fetch that's still
        // current gets to clear the indicator, so it stays visible for the
        // whole time a keystroke is outstanding, not just its first fetch.
        if (abortController.signal.aborted) return;
        this.#setLoading(false);
      });
  }

  /**
   * Toggles the busy indicator on the search icon while a predictive search
   * fetch is in flight.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);
    this.refs.predictiveSearchResults?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  /**
   * Restores the last completed search (term + rendered results) from
   * localStorage, if one exists and hasn't expired, so it's what's showing
   * the next time search is opened — e.g. after clicking a result and
   * navigating to a new page. Re-fetches in the background afterwards so
   * anything that's since gone stale (stock, price, new matches) catches up.
   * @returns {boolean} Whether a cached search was restored.
   */
  #restoreSearchFromCache() {
    const cached = this.#readSearchCache();
    if (!cached) return false;

    const { term, html } = cached;
    const { searchInput, predictiveSearchResults } = this.refs;

    searchInput.value = term;
    this.#showResetButton();
    morph(predictiveSearchResults, html);
    this.#resetScrollPositions();

    this.#getSearchResults(term);

    return true;
  }

  /**
   * @returns {{ term: string, html: string } | null}
   */
  #readSearchCache() {
    try {
      const raw = localStorage.getItem(PredictiveSearchComponent.#SEARCH_CACHE_KEY);
      if (!raw) return null;

      const cached = JSON.parse(raw);
      if (
        !cached ||
        typeof cached.term !== 'string' ||
        typeof cached.html !== 'string' ||
        typeof cached.timestamp !== 'number'
      ) {
        return null;
      }

      if (Date.now() - cached.timestamp > PredictiveSearchComponent.#SEARCH_CACHE_TTL_MS) {
        return null;
      }

      return { term: cached.term, html: cached.html };
    } catch {
      // Malformed cache entry or localStorage unavailable — treat as no cache.
      return null;
    }
  }

  /**
   * @param {string} term
   * @param {string} html
   */
  #writeSearchCache(term, html) {
    try {
      localStorage.setItem(
        PredictiveSearchComponent.#SEARCH_CACHE_KEY,
        JSON.stringify({ term, html, timestamp: Date.now() })
      );
    } catch {
      // Private browsing, quota exceeded, etc. — caching is a nice-to-have,
      // not worth surfacing an error over.
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    // #createAbortController() aborts any in-flight #getSearchResults()
    // fetch, but that fetch's own .finally() no-ops once it sees itself
    // aborted (so a stale response can't clobber newer results) — so
    // nothing else would ever clear the spinner it turned on. Clearing the
    // query means we're no longer busy searching, so do it here instead.
    this.#setLoading(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
